import React from 'react'
import { Button, Form, Input, Typography, Upload, message } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { productApi } from 'api/product'
import { ICategory, IFrontCategory } from 'models/product'
import removePhoto from 'assets/icons/remove-photo.svg'
import { IMAGES_URL } from 'shared/constants/urls'
import { showServerError } from 'shared/utils/showServerError'
import { normalizeFile } from 'shared/utils/normalizeFile'
import { showMessage } from 'shared/utils/showMessage'

import { SeoFields } from 'components/ui'
import { ChangeDataWrapper } from '../ChangeDataWrapper/ChangeDataWrapper'
import styles from './ChangeCategory.module.scss'

interface IChangeCategoryProps {
  currentCategory: IFrontCategory
  onSuccess: (updatedCategory: ICategory) => void
}

interface IFormData {
  name: string
  photo: any
  seoInfo?: {
    title?: string
    desc?: string
    h1?: string
    seoText?: string
  }
}

export const ChangeCategory: React.FC<IChangeCategoryProps> = React.memo(
  ({ currentCategory, onSuccess }) => {
    const [isLoading, setIsLoading] = React.useState(false)
    const [deletedPhoto, setDeletedPhoto] = React.useState<string>('')

    const [form] = Form.useForm()

    const photo =
      Form.useWatch('photo', {
        form,
        preserve: true
      }) ?? []

    React.useEffect(() => {
      form.resetFields()
      form.setFieldsValue({
        name: currentCategory.name,
        seoInfo: {
          title: currentCategory?.seoInfo?.title,
          desc: currentCategory?.seoInfo?.desc,
          h1: currentCategory?.seoInfo?.h1,
          seoText: currentCategory?.seoInfo?.seoText
        }
      })

      if (currentCategory.photo) {
        form.setFieldValue('photo', [
          {
            uid: currentCategory.photo,
            thumbUrl: `${IMAGES_URL}${currentCategory.photo}`
          }
        ])
      }
    }, [currentCategory])

    const onRemovePhoto = (file: any) => {
      if (!file.originFileObj) {
        setDeletedPhoto(file.uid)
      }
    }

    const onSubmit = async (data: IFormData) => {
      try {
        setIsLoading(true)

        const commonData = {
          name: data.name,
          photo: data.photo[0].originFileObj ?? data.photo[0].uid,
          title: data?.seoInfo?.title,
          desc: data?.seoInfo?.desc,
          h1: data?.seoInfo?.h1,
          seoText: data?.seoInfo?.seoText
        }

        if (currentCategory.isNew) {
          const response = await productApi.createCategory(commonData)
          onSuccess(response.data)
          message.success('Категория успешно добавлена')
        } else {
          const response = await productApi.updateCategory(
            currentCategory._id,
            {
              ...commonData,
              toDeletePhoto: deletedPhoto
            }
          )
          onSuccess(response.data)
          message.success('Категория успешно обновлена')
        }
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoading(false)
      }
    }

    return (
      <ChangeDataWrapper title='Основная категория'>
        <Form
          form={form}
          name='changeCategory'
          size='small'
          onFinish={onSubmit}
        >
          <div style={{ display: 'flex' }}>
            <Form.Item
              name='photo'
              valuePropName='fileList'
              getValueFromEvent={normalizeFile}
              rules={[
                {
                  required: true,
                  message: 'Добавьте изображение категории'
                }
              ]}
              style={{ flex: 1 }}
              hasFeedback
            >
              <Upload.Dragger
                multiple={false}
                maxCount={1}
                accept='image/png, image/jpeg, image/jpg'
                listType='picture'
                beforeUpload={(file) => {
                  const isValid =
                    file.type === 'image/png' ||
                    file.type === 'image/jpg' ||
                    file.type === 'image/jpeg'
                  if (!isValid) {
                    showMessage(
                      'error',
                      'Поддерживаются только форматы JPG, JPEG, PNG',
                      ''
                    )
                  }
                  return isValid || Upload.LIST_IGNORE
                }}
                customRequest={() => false}
                itemRender={(_, file, fileList, { remove }) => {
                  return (
                    <div className={styles.photoWrapper}>
                      <img
                        src={file.thumbUrl}
                        alt='Изображение'
                        width={84}
                        height={82}
                        style={{ objectFit: 'contain' }}
                      />
                      <button
                        type='button'
                        className={classNames(
                          'reset-button',
                          styles.removePhoto
                        )}
                        onClick={() => {
                          onRemovePhoto(file)
                          remove()
                        }}
                      >
                        <img src={removePhoto} alt='Удалить изображение' />
                      </button>
                    </div>
                  )
                }}
                className={classNames({
                  [styles.uploaderWrapperWithPhoto]: !!photo.length
                })}
              >
                <div className={styles.uploader}>
                  <InboxOutlined style={{ color: '#1677FF', fontSize: 48 }} />
                  <Typography.Text style={{ textAlign: 'left' }}>
                    Нажмите или перетащите файл в эту <br /> область, чтобы
                    загрузить
                  </Typography.Text>
                </div>
              </Upload.Dragger>
            </Form.Item>
          </div>
          <Form.Item
            label='Название категории'
            name='name'
            rules={[
              {
                required: true,
                message: 'Введите название'
              },
              {
                whitespace: true,
                message: 'Введите название'
              }
            ]}
            hasFeedback
          >
            <Input />
          </Form.Item>
          <SeoFields
            formItemClassName={styles.formLabel}
            inputClassName={styles.formInput}
            textareaClassName={styles.formTextarea}
            panelHeaderClassName={styles.panelHeader}
          />
          <div className={styles.footer}>
            <Button
              htmlType='submit'
              type='primary'
              size='middle'
              loading={isLoading}
            >
              Сохранить изменения
            </Button>
            <Button
              danger
              disabled
              htmlType='submit'
              type='primary'
              size='middle'
            >
              Удалить
            </Button>
          </div>
        </Form>
      </ChangeDataWrapper>
    )
  }
)
